// Copyright (C) 2020-2022 Intel Corporation
// Copyright (C) 2022-2023 CVAT.ai Corporation
//
// SPDX-License-Identifier: MIT

import 'components/task-page/styles.scss';
import 'components/project-page/styles.scss';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router';
import { Link } from 'react-router-dom';
import Button from 'antd/lib/button';
import Dropdown from 'antd/lib/dropdown';
import Menu from 'antd/lib/menu';
import Modal from 'antd/lib/modal';
import notification from 'antd/lib/notification';
import Result from 'antd/lib/result';
import Text from 'antd/lib/typography/Text';
import Title from 'antd/lib/typography/Title';
import { MoreOutlined } from '@ant-design/icons';

import { getCore, Reference, Task } from 'cvat-core-wrapper';
import { CombinedState } from 'reducers';
import { deleteReferenceAsync } from 'actions/references-actions';
import { getInferenceStatusAsync } from 'actions/models-actions';
import ActionsMenuContainer from 'containers/actions-menu/actions-reference-menu';
import ModelRunnerModal from 'components/model-runner-modal/model-runner-dialog-ref';
import MoveReferenceModal from 'components/move-reference-modal/move-reference-modal';
import Preview from 'components/common/preview';
import { openAddImagesModal } from 'components/annotation-page/workspace/add-images-modal';
import { referencePath } from 'components/project-page/reference-row';
import { taskPath } from 'components/project-page/task-row';
import FrameTiles from 'components/task-page/frame-tiles';
import {
    CategoriesSkeleton, DetailPageSkeleton, RowSkeletons,
} from 'components/common/skeletons';
import { createdLabel, plural } from 'components/task-page/task-model';
import UserSelector from 'components/task-page/user-selector';
import { workspacePath } from 'utils/workspace-route';
import { fromNow, t } from 'utils/i18n';
import LinkTaskModal from './link-task-modal';
import {
    Coverage, coverage, coverageLabel, LabelInfo, RawShape,
} from './reference-model';

const core = getCore();
const SHOWN_CATEGORIES = 8;

function ReferencePageComponent(): JSX.Element {
    const id = +useParams<{ id: string }>().id;
    const history = useHistory();
    const dispatch = useDispatch();
    const deletes = useSelector((state: CombinedState) => state.references.activities.deletes);
    const [reference, setReference] = useState<Reference | null>(null);
    const [loading, setLoading] = useState(true);
    const [projectName, setProjectName] = useState<string | null>(null);
    const [detections, setDetections] = useState<Coverage | null>(null);
    const [tasks, setTasks] = useState<Task[] | null>(null);
    const [linking, setLinking] = useState(false);
    const [allCategories, setAllCategories] = useState(false);
    // a failed request ends its skeleton with a message instead of leaving it forever
    const [detectionsFailed, setDetectionsFailed] = useState(false);
    const listedName = useSelector((state: CombinedState) => (
        state.projects.current.find((item: { id: number }) => item.id === reference?.projectId)?.name
    ));

    const loadTasks = useCallback((loaded: Reference): void => {
        const fetches = (loaded.taskId ?? []).map((taskId) => (
            core.tasks.get({ id: taskId }).then(([task]: Task[]) => task)
        ));
        // a task the user may not open (or one that is gone) must not hold the others back
        Promise.allSettled(fetches).then((results) => setTasks(results.flatMap((result) => (
            result.status === 'fulfilled' && result.value ? [result.value] : []
        ))));
    }, []);
    const reload = useCallback((): void => {
        core.references.get({ id })
            .then(([loaded]: Reference[]) => { if (loaded) { setReference(loaded); loadTasks(loaded); } })
            .catch(() => undefined);
    }, [id, loadTasks]);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        core.references.get({ id })
            .then(([loaded]: Reference[]) => {
                if (cancelled || !loaded) return;
                setReference(loaded);
                loadTasks(loaded);
                if (loaded.projectId) {
                    core.projects.get({ id: loaded.projectId })
                        .then(([project]: { name: string }[]) => {
                            if (!cancelled && project) setProjectName(project.name);
                        })
                        .catch((): void => undefined);
                }
                const [job] = loaded.jobs;
                if (!job) setDetections(coverage([], []));
                else {
                    core.server.request(`${core.config.backendAPI}/reference_jobs/${job.id}/annotations`, { method: 'GET' })
                        .then((response: { data: { shapes: RawShape[] } }) => {
                            if (!cancelled) setDetections(coverage(response.data.shapes, loaded.labels as LabelInfo[]));
                        })
                        .catch((): void => { if (!cancelled) setDetectionsFailed(true); });
                }
            })
            .catch(() => undefined)
            .finally(() => { if (!cancelled) setLoading(false); });
        dispatch(getInferenceStatusAsync());
        return () => { cancelled = true; };
    }, [id]);

    useEffect(() => {
        if (reference && deletes[id]) history.push(reference.projectId ? `/projects/${reference.projectId}` : '/projects');
    }, [deletes]);

    if (loading) {
        return (
            <DetailPageSkeleton
                primary={t('Open photos')}
                strip={{ title: t('Photos'), action: t('Add photos') }}
                middle={{ title: t('Detections'), body: <CategoriesSkeleton rows={8} /> }}
                last={t('Tasks')}
            />
        );
    }
    if (!reference) {
        return <Result className='cvat-not-found' status='404' title={t('The reference was not found')} />;
    }

    const linked = reference.taskId ?? [];
    const save = async (apply: (target: Reference) => void): Promise<void> => {
        try {
            apply(reference);
            setReference(await reference.save());
        } catch (error: unknown) {
            notification.error({ message: t('Could not update the reference'), description: String(error) });
        }
    };
    const setLinks = async (ids: number[]): Promise<void> => {
        try {
            await core.server.request(`${core.config.backendAPI}/references/${id}`, { method: 'PATCH', data: { tasks: ids } });
            reload();
        } catch (error: unknown) {
            notification.error({ message: t('Could not change the linked tasks'), description: String(error) });
        }
    };
    const confirmUnlink = (task: Task): void => {
        Modal.confirm({
            title: t('Unlink “{name}”?', { name: task.name }),
            content: t('The photos stay; they are no longer shown with that task.'),
            okText: t('Unlink'),
            onOk: () => setLinks(linked.filter((item) => item !== task.id)),
        });
    };
    const confirmDelete = (): void => {
        Modal.confirm({
            title: t('Delete the reference “{name}”?', { name: reference.name }),
            content: t('The photos and their detections are removed. The tasks stay.'),
            okText: t('Delete'),
            okButtonProps: { danger: true },
            onOk: () => dispatch(deleteReferenceAsync(reference)),
        });
    };

    const [job] = reference.jobs;
    const workspace = referencePath(reference);
    const meta = [plural(reference.size, 'photo'), coverageLabel(detections)].filter(Boolean).join(' · ');
    const categories = detections?.categories ?? [];
    const shownCategories = allCategories ? categories : categories.slice(0, SHOWN_CATEGORIES);

    return (
        <div className='cvat-task-page'>
            <div className='cvat-task-page-content'>
                <nav aria-label={t('Breadcrumb')} className='cvat-task-page-crumbs'>
                    <Link to='/projects'>{t('Projects')}</Link>
                    <span>/</span>
                    {reference.projectId ? (
                        <Link to={`/projects/${reference.projectId}`}>{listedName ?? projectName ?? '…'}</Link>
                    ) : <span>{t('No project')}</span>}
                    <span>/</span>
                    <span className='cvat-task-page-crumb-current'>{reference.name}</span>
                </nav>
                <div className='cvat-task-page-heading'>
                    <div className='cvat-task-page-heading-text'>
                        <Title
                            level={1}
                            editable={{
                                onChange: (value: string): void => {
                                    const name = value.trim();
                                    if (name && name !== reference.name) save((target) => { target.name = name; });
                                },
                            }}
                        >
                            {reference.name}
                        </Title>
                        <Text type='secondary'>{meta}</Text>
                    </div>
                    <Button type='primary' onClick={() => history.push(workspace)}>{t('Open photos')}</Button>
                    <Dropdown
                        destroyPopupOnHide
                        trigger={['click']}
                        overlay={(
                            <ActionsMenuContainer
                                referenceInstance={reference}
                                onViewAnalytics={() => history.push(`/references/${id}/analytics`)}
                            />
                        )}
                    >
                        <Button aria-label={t('More actions')} icon={<MoreOutlined />} />
                    </Dropdown>
                </div>

                <div className='cvat-task-page-columns'>
                    <div className='cvat-task-page-main'>
                        <section aria-label={t('Photos')} className='cvat-task-page-section'>
                            <div className='cvat-task-page-section-head'>
                                <h2>{t('Photos')}</h2>
                                <Text type='secondary'>
                                    {reference.size > 6 ?
                                        t('{count} · the first few; all of them are in the workspace', { count: reference.size }) :
                                        String(reference.size)}
                                </Text>
                                <span className='cvat-task-page-spacer' />
                                <Button onClick={() => openAddImagesModal(`/references/${id}/data`, 'photos')}>{t('Add photos')}</Button>
                            </div>
                            {job && reference.size > 0 ? (
                                <FrameTiles
                                    session='reference_jobs'
                                    jobId={job.id}
                                    count={reference.size}
                                    status={detections?.frames}
                                    path={workspace}
                                />
                            ) : (
                                <Text type='secondary'>{t('No photos yet.')}</Text>
                            )}
                        </section>

                        <section aria-label={t('Detections')} className='cvat-task-page-section'>
                            <div className='cvat-task-page-section-head'>
                                <h2>{t('Detections')}</h2>
                                {detections && (
                                    <Text type='secondary'>
                                        {t('{done} of {total} have a library model', { done: detections.withModel, total: detections.total })}
                                    </Text>
                                )}
                                <span className='cvat-task-page-spacer' />
                                <Link to={workspace}>{t('Find models in the workspace →')}</Link>
                            </div>
                            {!detections && !detectionsFailed && <CategoriesSkeleton rows={8} />}
                            {detectionsFailed && <Text type='secondary'>{t('Could not load the detections.')}</Text>}
                            {detections && detections.total > 0 && (
                                <div className='cvat-reference-page-bar' aria-hidden>
                                    <span style={{ width: `${(100 * detections.withModel) / detections.total}%` }} />
                                </div>
                            )}
                            {detections && detections.total === 0 && (
                                <Text type='secondary'>{t('Nothing detected yet. Run Detect furniture in the workspace.')}</Text>
                            )}
                            {shownCategories.length > 0 && (
                                <div className='cvat-reference-page-categories'>
                                    {shownCategories.map((category) => (
                                        <div key={category.name} className='cvat-reference-page-category'>
                                            <span className='cvat-reference-page-swatch' style={{ background: category.color }} />
                                            <span className='cvat-task-page-step-title'>{category.name}</span>
                                            <Text type='secondary'>{t('{done} of {total}', { done: category.withModel, total: category.total })}</Text>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {categories.length > SHOWN_CATEGORIES && (
                                <Button type='link' size='small' className='cvat-reference-page-more' onClick={() => setAllCategories(!allCategories)}>
                                    {allCategories ?
                                        t('Fewer categories') :
                                        t('{n} more categories', { n: categories.length - SHOWN_CATEGORIES })}
                                </Button>
                            )}
                        </section>

                        <section aria-label={t('Tasks')} className='cvat-task-page-section'>
                            <div className='cvat-task-page-section-head'>
                                <h2>{t('Tasks')}</h2>
                                <Text type='secondary'>{t('drawings these photos belong to')}</Text>
                                <span className='cvat-task-page-spacer' />
                                <Button onClick={() => setLinking(true)}>{t('Link a task')}</Button>
                            </div>
                            {tasks === null && <RowSkeletons count={Math.max(1, linked.length)} />}
                            {tasks && tasks.length > 0 && (
                                <div className='cvat-project-task-list'>
                                    {tasks.map((task) => {
                                        const taskJob = task.jobs.find((item) => !item.type || item.type === 'annotation') ?? task.jobs[0];
                                        const open = taskJob && job ?
                                            workspacePath(task.id, taskJob.id, reference.id) :
                                            taskPath(task);
                                        return (
                                            <div key={task.id} className='cvat-project-task-row' data-task={task.id}>
                                                <Preview
                                                    task={task}
                                                    loadingClassName='cvat-project-task-preview-loading'
                                                    emptyPreviewClassName='cvat-project-task-preview-empty'
                                                    previewWrapperClassName='cvat-project-task-preview'
                                                    previewClassName='cvat-project-task-preview-image'
                                                    onClick={() => history.push(open)}
                                                />
                                                <div className='cvat-project-task-body'>
                                                    <Link to={`/tasks/${task.id}`} className='cvat-project-task-title'>{task.name}</Link>
                                                    <Text type='secondary'>
                                                        {`${plural(task.size, 'drawing')} · ${t('updated {ago}', { ago: fromNow(task.updatedDate) })}`}
                                                    </Text>
                                                </div>
                                                <Button onClick={() => history.push(open)}>
                                                    {t('Open with these photos')}
                                                </Button>
                                                <Dropdown
                                                    destroyPopupOnHide
                                                    trigger={['click']}
                                                    overlay={(
                                                        <Menu>
                                                            <Menu.Item key='unlink' onClick={() => confirmUnlink(task)}>{t('Unlink')}</Menu.Item>
                                                        </Menu>
                                                    )}
                                                >
                                                    <Button type='text' size='small' aria-label={t('More')} icon={<MoreOutlined />} />
                                                </Dropdown>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                            {tasks && tasks.length === 0 && (
                                <div className='cvat-task-page-empty'>
                                    <Text type='secondary'>
                                        {t('Not linked to a task yet. The photos open inside a task, next to its drawings.')}
                                    </Text>
                                    <div className='cvat-task-page-empty-actions'>
                                        <Button type='primary' onClick={() => setLinking(true)}>{t('Link a task')}</Button>
                                    </div>
                                </div>
                            )}
                        </section>
                    </div>

                    <aside aria-label={t('About this reference')} className='cvat-task-page-aside'>
                        <div className='cvat-task-page-fact'>
                            <Text type='secondary'>{t('Assignee')}</Text>
                            <UserSelector
                                value={reference.assignee}
                                ariaLabel={t('Assignee')}
                                onSelect={(user) => save((target) => { target.assignee = user; })}
                            />
                        </div>
                        <div className='cvat-task-page-fact'>
                            <Text type='secondary'>{t('Created')}</Text>
                            <span>
                                {createdLabel(reference.createdDate, reference.owner?.username)}
                            </span>
                        </div>
                        <div className='cvat-task-page-danger'>
                            <Button type='link' danger onClick={confirmDelete}>{t('Delete reference…')}</Button>
                        </div>
                    </aside>
                </div>
            </div>
            <ModelRunnerModal />
            <MoveReferenceModal onUpdateReference={(updated: Reference) => updated.save().then(setReference)} />
            {linking && reference.projectId && (
                <LinkTaskModal
                    projectId={reference.projectId}
                    referenceId={id}
                    linked={linked}
                    onClose={() => setLinking(false)}
                    onLinked={() => { setLinking(false); reload(); }}
                />
            )}
        </div>
    );
}

export default React.memo(ReferencePageComponent);
